'use client';

import Link from 'next/link';
import cn from 'classnames';
import Balancer from 'react-wrap-balancer';
import { usePathname } from 'next/navigation';

import { HvacLogoReverse } from '@/components/logos/HvacLogoReverse';
import { FooterLinkItem } from '../FooterLink/types';
import { FooterLink } from '../FooterLink';
import { SocialLinks } from '../SocialLinks';

import { useSplitAndHeap } from '@/hooks/useSplitAndHeap';

export const navLinks: FooterLinkItem[] = [
	{
		href: '/replace',
		label: 'Replace my HVAC',
	},
	{
		href: '/repair',
		label: 'Repair my HVAC',
	},
	{
		href: '/tools',
		label: 'Calculators',
	},
	{
		href: '/expert-advice',
		label: 'Learn about HVAC',
	},

	{
		href: '/about-us',
		label: 'About Us',
	},
	{
		href: '/privacy-policy',
		label: 'Privacy Policy',
		className: 'lg:ml-auto',
	},
	{
		href: '/terms-of-use',
		label: 'Terms of Use',
	},
];

const info = {
	site: 'HVAC.com',
	address: '800-E Beaty St, Davidson, NC 28036',
	copyright:
		'HVAC.com – a Magenta Technologies LLC Company, All Rights Reserved',
};

const Footer = () => {
	const pathname = usePathname();

	const { treatment: GlobalPhoneNumber, config: configString } =
		useSplitAndHeap('GlobalPhoneNumber').GlobalPhoneNumber;
	const treatmentObject = JSON.parse(configString as string);

	if (
		(pathname?.includes('/virtual/') ||
			pathname?.includes('/in-person/') ||
			pathname?.includes('/get-started/') ||
			pathname?.includes('/flow/')) &&
		!pathname?.includes('/confirmation')
	) {
		return <footer></footer>;
	}

	return (
		<footer className="mt-auto print:hidden">
			<div className={cn('gradient-nightmare')}>
				<div
					className={cn(
						'w-full max-w-7xl',
						'mx-auto px-4 py-8 md:px-6 2xl:px-0',
					)}
				>
					<div
						className={cn(
							'flex flex-col lg:flex-row',
							'border-slate-300 lg:border-b',
							'items-center lg:justify-between',
							'lg:pb-7',
						)}
					>
						<Link href={'/'}>
							<HvacLogoReverse className="h-9" />
						</Link>
						<SocialLinks containerClass="hidden lg:flex" />
					</div>
					<nav className={cn('pt-8')}>
						<ul
							className={cn(
								'flex flex-col items-center lg:flex-row',
								'gap-4 lg:gap-7',
							)}
						>
							{navLinks.map((link) => (
								<FooterLink key={link.label} link={link} />
							))}
						</ul>
					</nav>

					<div
						className={cn(
							'hidden flex-row justify-between lg:flex',
							'lg:mt-7',
							'text-xs tracking-tight text-slate-600 2xl:text-base',
						)}
					>
						<p>
							{info.site} • {info.address}
							{GlobalPhoneNumber === 'on'
								? ` • ${treatmentObject?.mainPhoneNumber}`
								: ''}
						</p>

						<p>
							&copy; {new Date().getFullYear()} {info.copyright}
						</p>
					</div>

					{/* For mobile only */}
					<SocialLinks containerClass="flex justify-center py-8 lg:hidden" />
					<div
						className={cn(
							'text-slate-600',
							'lg:hidden',
							'text-center',
							'border-b border-slate-300',
							'pb-10',
						)}
					>
						<p>{info.site}</p>
						<p>{info.address.replace(',', '\n')}</p>

						{GlobalPhoneNumber === 'on' && (
							<p>
								<Link
									href={`tel:${treatmentObject?.mainPhoneNumber}`}
									className="text-black"
								>
									{treatmentObject?.mainPhoneNumber}
								</Link>
							</p>
						)}
					</div>
					<div
						className={cn('text-slate-400', 'lg:hidden', 'text-center', 'py-6')}
					>
						<p>
							<Balancer>
								&copy; {new Date().getFullYear()} {info.copyright}
							</Balancer>
						</p>
					</div>
				</div>
			</div>
		</footer>
	);
};

export default Footer;
